
import { computed, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  MessageType,
  TelephonyType,
  AwsLanguage
} from "@moderrotech/servers-communication-library/src/types";
import _ from "lodash";
import { showToastrError } from "@/composables/notifications";
import moment from "moment/moment";
import { i18n } from "@/lang";

export default {
  setup() {
    const status = ref<"pending" | "transcription" | "translating">("pending");

    const store = useStore();

    const microphones = computed<
        {
          microphone: MediaDeviceInfo;
          isActive: boolean;
        }[]
      >(() => store.state.session.mediaInputs.microphones),
      microphoneId = computed(
        () =>
          _.find(microphones.value, m => m.isActive)?.microphone.deviceId ??
          null
      );

    const getProperty = (name: string) => store.getters["auth/property"](name),
      sourceLanguage = computed(
        (): keyof typeof AwsLanguage | null =>
          store.state.session.sourceLanguage ??
          (getProperty("aws.translation.source.language") !== null
            ? AwsLanguage[getProperty("aws.translation.source.language")]
            : null)
      ),
      targetLanguage = computed(
        (): AwsLanguage | null => store.state.session.targetLanguage
      );

    const isTranslatorEnabled = computed(() =>
      getProperty("aws.translator.enabled")
    );

    const sourceLanguages = _.map(
      _.filter(
        _.entries(AwsLanguage).slice(_.entries(AwsLanguage).length / 2),
        pair => (pair[1] as AwsLanguage) <= AwsLanguage["zh-HK"]
      ),
      pair => pair[0] as keyof typeof AwsLanguage
    );

    watch(
      () => sourceLanguage.value,
      v => {
        if (v) {
          if (isTranslatorEnabled.value && !sourceLanguages.includes(v)) {
            showToastrError(
              i18n.global.t("call.error.recordTranslation.unsupportedLanguage")
            );
          }

          store.dispatch("session/sendJsonMessage", {
            message: {
              type: MessageType.awsTranslationTargetLanguage,
              languageCode: v
            }
          });
        }
      },
      { immediate: true }
    );

    const transcription = computed(
      (): string | null => store.state.session.recordTranscription
    );
    watch(
      () => status.value === "pending",
      v => {
        if (v) {
          store.commit("session/UNSET_RECORD_TRANSCRIPTION");
        }
      },
      { immediate: true }
    );

    const errCb = (
      consoleMessage: string,
      toastrMessage: string,
      err?: any
    ) => {
      console.error(`${consoleMessage}: `, err);
      showToastrError(toastrMessage);
    };

    const audioContext = new AudioContext();

    const handleStartTranscriptionClick = async () => {
        status.value = "transcription";

        let stream: MediaStream | null =
          store.state.telephonyType === TelephonyType.webex &&
          store.state.webex.streams.localVideo?.outputStream
            ? (store.state.webex.streams.localVideo
                ?.outputStream as MediaStream).clone()
            : null;

        if (!stream) {
          if (!microphones.value) {
            await store.dispatch("session/mediaInputs/getMicrophones");
          }

          const constraints = {
            audio: microphoneId.value ? { deviceId: microphoneId.value } : true,
            video: false
          };

          console.log("Getting audio stream... ");

          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
          } catch (err) {
            errCb(
              "Can not get audio stream",
              i18n.global.t("call.error.recordTranslation.streamGetting"),
              err
            );
            status.value = "pending";
            return;
          }
        }

        try {
          await store.dispatch("session/startTranscription", stream);
        } catch (err) {
          errCb(
            "Can not start transcription",
            i18n.global.t("call.error.recordTranslation.transcription"),
            err
          );
          status.value = "pending";
        }
      },
      stopTranscription = async () =>
        await store.dispatch("session/stopTranscription"),
      handleTranslateClick = async () => {
        status.value = "translating";

        await stopTranscription();

        const isActual = () => status.value === "translating";

        if (!isActual()) {
          return;
        }

        if (!transcription.value) {
          errCb(
            "Can not translate the record",
            i18n.global.t("call.error.recordTranslation.translation")
          );
          status.value = "pending";
          return;
        }

        const data: { text: string; audio?: string } = await store
          .dispatch("session/translate", transcription.value)
          .catch(err => {
            errCb(
              "Can not translate the record",
              i18n.global.t("call.error.recordTranslation.translation"),
              err
            );
            status.value = "pending";
          });

        if (!isActual()) {
          return;
        }

        store.dispatch("session/sendJsonMessage", {
          message: {
            type: MessageType.awsTranslationFinished,
            text: data.text,
            audioFilePath: data.audio ?? ""
          },
          callback: () => (status.value = "pending"),
          errorCallback: () => {
            errCb(
              "Can not send translation",
              i18n.global.t("call.error.recordTranslation.translation")
            );
            status.value = "pending";
          }
        });
      };

    const translation = computed(
      (): { text: string; audio?: Uint8Array } =>
        store.state.session.recordTranslation
    );

    const translations = ref<
      { timestamp: number; text: string; audio?: Uint8Array }[]
    >([]);

    let timer: number;
    const clearTimer = () => window.clearTimeout(timer);

    let audio: AudioBufferSourceNode | null;

    const isSubtitlesEnabled = computed(() =>
      getProperty("aws.displaying.translated.subtitles.enabled")
    );

    const stopPlaying = () => {
      audio?.stop();
      audio = null;
      clearTimer();
    };

    const runQueuedTranslation = () => {
      const tr = _.first(translations.value);

      if (!tr) {
        return;
      }

      const cb = () => {
        translations.value = _.filter(
          translations.value,
          v => v.timestamp !== tr.timestamp
        );
        runQueuedTranslation();
      };

      if (tr.audio && getProperty("aws.playing.translated.audio.enabled")) {
        const buffer = new Uint8Array(tr.audio.length);
        buffer.set(new Uint8Array(tr.audio), 0);
        audioContext
          .decodeAudioData(buffer.buffer)
          .then(audioBuffer => {
            audio = audioContext.createBufferSource();
            audio.loop = false;
            audio.onended = () => cb();
            audio.buffer = audioBuffer;
            audio.connect(audioContext.destination);
            audio.start(0);
          })
          .catch(err =>
            errCb(
              "Can not play translation audio",
              i18n.global.t("call.error.recordTranslation.play"),
              err
            )
          );
      } else if (isSubtitlesEnabled.value) {
        timer = window.setTimeout(() => cb(), tr.text.length * 1000);
      }
    };

    watch(
      () => translation.value,
      v => {
        if (v) {
          translations.value.push(_.assign({ timestamp: +moment() }, v));

          if (translations.value.length <= 1) {
            runQueuedTranslation();
          }
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      if (status.value === "transcription") {
        stopTranscription();
      }
      stopPlaying();
      store.commit("session/UNSET_RECORD_TRANSLATION");
      status.value = "pending";
    });

    return {
      status,
      handleStartTranscriptionClick,
      handleTranslateClick,
      isTranslatorEnabled,
      isSubtitlesEnabled,
      transcription,
      translations,
      sourceLanguage,
      targetLanguage,
      getProperty,
      sourceLanguages,
      handleSourceLanguageChange: (e: Event) =>
        store.commit("session/SET_SOURCE_LANGUAGE", _.get(e, "target.value")),
      AwsLanguage,
      ..._.pick(_, ["first", "entries"])
    };
  }
};
